/** @file KeyRows: one row of key dots per keyed (or selected) node plus a summary row; select, drag, menu. */
import { keyedNodes } from '@zilo/pose3d-commands/clip/keyedNodes';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { CSSProperties, MouseEvent, ReactElement } from 'react';
import { useRef } from 'react';
import type { Selection } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { FRAME_WIDTH } from './frameWidth';
import { keyDots } from './keyDots';
import { mirrorGuard } from './mirrorGuard';
import { mirrorStart } from './mirrorStart';
import { moveKeyCommand } from './moveKeyCommand';
import type { PoseClipboard } from './poseClipboard';
import { timelineMenu } from './timelineMenu';
import { usePlayheadClip } from './usePlayheadClip';

/** A dot being dragged: its row (null = summary) and frame. */
type Drag = { node: string | null; frame: number };
/** What a track reports back to the rows. */
type TrackEvents = {
  onDot: (node: string | null, frame: number, add: boolean) => void;
  onGrab: (node: string | null, frame: number) => void;
  onDrop: (frame: number) => void;
  onMenu: (node: string | null, frame: number, e: MouseEvent) => void;
};
/** One track's data. */
type TrackProps = { node: string | null; clip: Clip; dots: number[]; current: number; selected: Selection['keys']; events: TrackEvents };

function cellStyle(f: number, current: number, mirrored: number): CSSProperties {
  return {
    width: FRAME_WIDTH, flex: 'none', height: 16, position: 'relative', borderLeft: f % 5 === 0 ? '1px solid var(--border, #2a2e38)' : 'none',
    background: f >= mirrored ? 'var(--grey-bg, #23262e)' : f === current ? 'var(--playhead-bg, #2b2a22)' : 'transparent',
  };
}

function dotStyle(selected: boolean, summary: boolean): CSSProperties {
  return {
    position: 'absolute', left: 2, top: 3, width: 9, height: 9, transform: 'rotate(45deg)', cursor: 'grab',
    background: selected ? 'var(--accent, #f2b544)' : summary ? 'var(--muted, #8b919e)' : 'var(--info, #7fd1ff)',
  };
}

/** Cells 0..frames with dots; greyed from the mirror start; every cell is a drop target and has the menu. */
function KeyTrack({ node, clip, dots, current, selected, events }: TrackProps): ReactElement {
  const mirrored = mirrorStart(clip) ?? Infinity;
  const isSelected = (f: number): boolean => selected.some((k) => k.frame === f && (node === null || k.node === node));
  return (
    <div style={{ display: 'flex' }}>
      {Array.from({ length: clip.frames + 1 }, (_, f) => (
        <div key={f} data-frame={f} style={cellStyle(f, current, mirrored)}
          onPointerUp={() => { events.onDrop(f); }} onContextMenu={(e) => { events.onMenu(node, f, e); }}>
          {dots.includes(f) && (
            <span role="button" aria-label={`${node ?? 'all'} key ${f}`} aria-pressed={isSelected(f)} style={dotStyle(isSelected(f), node === null)}
              onPointerDown={() => { events.onGrab(node, f); }} onClick={(e) => { events.onDot(node, f, e.shiftKey); }} />
          )}
        </div>
      ))}
    </div>
  );
}

/** The summary row's dots: every key frame of the first half. */
const allDots = (clip: Clip): number[] => {
  const end = mirrorStart(clip) ?? Infinity;
  return clip.keys.map((k) => k.frame).filter((f) => f < end);
};

/** Rows for the playhead clip; dragging a node dot moves that key, dragging a summary dot shifts the whole key. */
export function KeyRows({ clipboard }: { clipboard: PoseClipboard }): ReactElement | null {
  const store = useEditorStore();
  const cur = usePlayheadClip();
  const frame = useEditor((s) => s.playhead.frame);
  const selection = useEditor((s) => s.selection);
  const drag = useRef<Drag | null>(null);
  if (cur === null) return <div style={{ padding: 8, color: 'var(--muted, #8b919e)' }}>Pick a clip</div>;
  const { name, clip } = cur;
  const rows = [...new Set([...keyedNodes(clip), ...selection.nodes])];
  const events: TrackEvents = {
    onDot: (node, f, add) => {
      const picked = node === null ? Object.keys(clip.keys.find((k) => k.frame === f)?.nodes ?? {}).map((n) => ({ node: n, frame: f })) : [{ node, frame: f }];
      store.getState().select({ keys: add ? [...selection.keys, ...picked] : picked });
    },
    onGrab: (node, f) => { drag.current = { node, frame: f }; },
    onDrop: (to) => {
      const d = drag.current;
      drag.current = null;
      if (d === null || d.frame === to || mirrorGuard(clip, to, store.getState().notify)) return;
      const command = d.node === null
        ? { name: 'clip.shiftFrames' as const, input: { clip: name, from: d.frame, to: d.frame, by: to - d.frame } }
        : moveKeyCommand(name, clip, d.node, d.frame, to);
      if (command !== null) void store.getState().dispatch([command], `move key ${d.frame} -> ${to}`);
    },
    onMenu: (node, f, e) => { e.preventDefault(); store.getState().openMenu(e.clientX, e.clientY, timelineMenu(store, clipboard, node, f)); },
  };
  const label: CSSProperties = { width: 90, flex: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 4 };
  return (
    <div onPointerUp={() => { drag.current = null; }} style={{ fontSize: 11 }}>
      <div style={{ display: 'flex', fontWeight: 600 }}><span style={label}>all</span>
        <KeyTrack node={null} clip={clip} dots={allDots(clip)} current={frame} selected={selection.keys} events={events} /></div>
      {rows.map((node) => (
        <div key={node} data-row={node} style={{ display: 'flex', color: selection.nodes.includes(node) ? 'var(--accent, #f2b544)' : 'inherit' }}>
          <span style={label} onClick={(e) => { store.getState().select({ nodes: e.shiftKey ? [...selection.nodes, node] : [node], drawable: null }); }}>{node}</span>
          <KeyTrack node={node} clip={clip} dots={keyDots(clip, node)} current={frame} selected={selection.keys} events={events} />
        </div>
      ))}
    </div>
  );
}
